/* eslint-disable max-len */
import { useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import useOutsideClick from "hooks/useOutsideClick";
import { useEditContext } from "react-admin";
import { useFormContext } from "react-hook-form";
import { IconButton } from "@mui/material";

interface IOptionData {
  label: string;
  value: string;
}

interface ICustomPronounSelectData {
  source: string;
  label?: string;
  className?: string;
  errorMsg?: string;
}

const options: IOptionData[] = [
  {
    label: "She/Her",
    value: "She/Her"
  },
  {
    label: "He/Him",
    value: "He/Him"
  },
  {
    label: "They/Them",
    value: "They/Them"
  },
  {
    label: "Other",
    value: "Other"
  }
];

const CustomPronounSelect = ({
  label = "",
  className = "",
  errorMsg,
  source
}: ICustomPronounSelectData) => {
  const { record } = useEditContext();
  const [value, setValue] = useState<string>(
    record?.[source] ? record?.[source] : ""
  );
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isOther, setIsOther] = useState<string>("");
  const ref = useRef<HTMLButtonElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const form = useFormContext();

  useOutsideClick(ref, () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const handleOnChange = (optionValue: string) => {
    if (optionValue === "Other") {
      setIsOther("Other");
      setValue("");
      form.setValue(source, "");
      setTimeout(() => {
        if (otherInputRef?.current) {
          otherInputRef.current.focus();
        }
      }, 0);
    } else {
      setValue(optionValue);
      form.setValue(source, optionValue, { shouldDirty: true });
      setIsOther("");
    }
  };

  const otherInputOnchange = (text: string) => {
    setValue(text);
    form.setValue(source, text, { shouldDirty: true });
  };
  const handleOpenOptions = () => {
    setIsOpen((prevState) => !prevState);
    setTimeout(() => {
      window?.scrollTo({
        top: document?.body?.scrollHeight,
        left: 0,
        behavior: "smooth"
      });
    }, 0);
  };
  return (
    <div
      className={className}
      style={{
        marginBottom: isOpen && isOther ? "110px" : isOpen ? "160px" : "0px"
      }}
    >
      <div className="relative">
        <button
          className={`w-full outline-none font-['Inter'] px-3 pt-[12px] cursor-pointer text-left text-[16px] font-['Inter'] font-normal ${
            !isOther && value === "" ? "pb-[30px]" : "pb-[4px]"
          } ${
            isOpen || isOther ? "rounded-t-lg" : "rounded-lg"
          } bg-[rgba(0,0,0,0.04)] border border-[rgba(48,47,46,0.06)] focus:!border-mono-primary hover:bg-[rgba(0,0,0,0.09)]`}
          onClick={handleOpenOptions}
          ref={ref}
          type="button"
        >
          <div>
            {label && (
              <label
                className={
                  value || isOther
                    ? "absolute top-[3px] left-[14px] font-['Inter']"
                    : "pointer-events-none absolute top-[16px] left-[14px] font-['Inter'] transition-all ease-in-out duration-150 caption text-[16px]"
                }
              >
                {label}
              </label>
            )}
          </div>
          <div className="mt-[8px]">{isOther || value}</div>
        </button>
        <div>
          {isOpen && (
            <div className="w-full absolute z-10 top-[49px] bg-[#FAF9F7] rounded-b-lg border border-[rgba(48,47,46,0.1)]">
              {options?.map((option: IOptionData) => (
                <button
                  className="py-[9px] pl-3 border-none cursor-pointer w-full text-left text-[16px] font-['Inter'] font-normal hover:bg-mono-50 focus:bg-mono-50"
                  key={option.value}
                  onClick={() => handleOnChange(option?.value)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div
          className={`${
            isOpen ? "rotate-180" : ""
          } absolute right-2 top-1/2 -translate-y-1/2`}
        >
          <IconButton
            onClick={handleOpenOptions}
            sx={{ width: 28, height: 28 }}
          >
            <ArrowDropDownIcon />
          </IconButton>
        </div>
      </div>
      <div>{errorMsg && <span>{errorMsg}</span>}</div>
      {isOther && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="w-full bg-[#FAF9F7]"
          aria-hidden
        >
          <input
            className="w-full rounded-b-lg outline-none px-3 py-3 bg-[rgba(0,0,0,0.04)] text-base font-['Inter'] font-normal border !border-[rgba(48,47,46,0.1)] focus:!border-mono-primary"
            value={value}
            onChange={(e) => otherInputOnchange(e.target.value)}
            placeholder="e.g. They/Their"
            ref={otherInputRef}
          />
        </div>
      )}
    </div>
  );
};

export default CustomPronounSelect;
